<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'reservation-details-popup-overlay': true, 'is-visible': isVisible }"
    @click.stop.prevent="popupClicked">
    <div :class="{ 'reservation-details-popup': true, 'is-visible': isVisible, 'is-loading': isLoading, 'has-header-buttons': userCanAddReservation && reservationData.status !== 1 }">
      <a
        href="#"
        class="reservation-details-popup-close"
        @click.prevent="close()">
        &times;
      </a>

      <h2>
        <template v-if="!isBlockedDate">
          {{ $t('reservationDetails.title') }}
        </template>
        <template v-else-if="isCompanyBlockedDate">
          {{ $t('reservationDetails.titleCompanyBlockedDate') }}
        </template>
        <template v-else>
          {{ $t('reservationDetails.titleBlockedDate') }}
        </template>
        <small>#{{ reservationData.id }}</small>

        <btn
          v-if="!cannotEditDailyReservation && isBlockedDate && userCanAddReservation"
          :type="unblockInProgress ? 'loader with-icon' : 'with-icon'"
          :title="$t('reservation.unblockDate')"
          @click.native="unblock"
          :disabled="unblockInProgress"
          :iconName="!unblockInProgress ? 'cross-circle' : 'loader'"
          iconColor="color-danger">
          {{ $t('reservation.unblockDate') }}
        </btn>

        <btn
          v-if="!cannotEditDailyReservation && !isBlockedDate && userCanAddReservation && reservationData.status !== 1 && reservationData.status !== 4 && reservationData.status !== 5"
          type="small blue with-icon"
          iconName="edit"
          iconColor="color-light"
          @click.native="changeReservationDate()">
          {{ $t('reservationDetails.changeDate') }}
        </btn>

        <btn
          v-if="!cannotEditDailyReservation && !isBlockedDate && userCanAddReservation && reservationData.status !== 1 && reservationData.status !== 4 && reservationData.status !== 5"
          type="small with-icon"
          iconName="layers-thin"
          @click.native="copyReservation()">
          {{ $t('reservationDetails.copy') }}
        </btn>
      </h2>

      <div
        v-if="isLoading"
        class="reservation-details-popup-empty-state">
        {{ $t('common.ui.loadingData') }}
      </div>

      <div class="reservation-details-popup-content">
        <dl>
          <dt
            v-if="!isBlockedDate"
            class="reservation-details-popup-status-label">
            {{ $t('reservationDetails.status') }}
          </dt>
          <dd
            v-if="!isBlockedDate"
            class="reservation-details-popup-status">
            <span :class="reservationStatusClass">
              {{ reservationStatus }}
            </span>

            <btn
              v-if="reservationData.status === 1 && userCanAddReservation"
              :type="acceptInProgress ? 'loader icon' : 'icon'"
              :title="$t('reservationDetails.acceptReservation')"
              @click.native="accept"
              :disabled="acceptInProgress || rejectInProgress"
              :iconName="!acceptInProgress ? 'ok-circle' : 'loader'"
              iconColor="color-success" />

            <btn
              v-if="reservationData.status === 1 && userCanAddReservation"
              :type="rejectInProgress ? 'loader icon' : 'icon'"
              :title="$t('reservationDetails.rejectReservation')"
              @click.native="reject"
              :disabled="acceptInProgress || rejectInProgress"
              :iconName="!rejectInProgress ? 'cross-circle' : 'loader'"
              iconColor="color-danger" />

            <btn
              v-if="!cannotEditDailyReservation && reservationData.status === 3 && userCanAddReservation"
              :type="rejectInProgress ? 'loader icon' : 'icon'"
              :title="$t('reservationDetails.cancelReservation')"
              @click.native="reject"
              :disabled="acceptInProgress || rejectInProgress"
              :iconName="!rejectInProgress ? 'cross-circle' : 'loader'"
              iconColor="color-danger" />
          </dd>
          <dt v-if="isCompanyBlockedDate">
            {{ $t('reservationDetails.blockingType') }}:
          </dt>
          <dd v-if="isCompanyBlockedDate">
            <template v-if="reservationData.blocking_type === 0">
              <template v-if="reservationData.is_daily === 0">
                {{ $t('reservationDetails.blockingTypeCalendarHourly') }}
              </template>
              <template v-else>
                {{ $t('reservationDetails.blockingTypeCalendarDaily') }}
              </template>
            </template>
            <template v-if="reservationData.blocking_type === 1">{{ $t('reservationDetails.blockingTypeService') }}</template>
            <template v-if="reservationData.blocking_type === 2">{{ $t('reservationDetails.blockingTypeWorker') }}</template>
          </dd>
          <dt>
            <template v-if="isCompanyBlockedDate">
              {{ $t('reservationDetails.reservationDateCompanyBlockade') }}
            </template>
            <template v-else>
              {{ $t('reservationDetails.reservationDate') }}
            </template>
          </dt>
          <dd>
            <template v-if="reservationData.is_daily || reservationData.type === 1">
              <span>
                <strong>{{ $t('day.from') }}</strong>
                {{ reservationDateStart }}
              </span><br>
              <span>
                <strong>{{ $t('day.to') }}</strong>
                {{ reservationDateEnd }}
              </span>
            </template>
            <template v-else>
              {{ reservationDate }}
            </template>
          </dd>
          <dt v-if="waitingListEnabled && reservationData.waiting_list_notified">
            {{ $t('reservationDetails.reservationWaitingListNotificationDate') }}
          </dt>
          <dd v-if="waitingListEnabled && reservationData.waiting_list_notified">
            {{ reservationDate }}
          </dd>
          <dt v-if="reservationData.type === 0">{{ $t('reservationDetails.reservationHour') }}</dt>
          <dd v-if="reservationData.type === 0">
            {{ startTime }} - {{ endTime }}
          </dd>
          <dt v-if="isBlockedDate && isCompanyBlockedDate">
            <template v-if="isCompanyBlockedDate">
              {{ $t('reservationDetails.blockedToDateCompanyBlockade') }}
            </template>
            <template v-else>
              {{ $t('reservationDetails.blockedToDate') }}
            </template>
          </dt>
          <dd v-if="isBlockedDate && isCompanyBlockedDate">
            {{ blockedToDate }}
          </dd>
          <dt v-if="!isBlockedDate && reservationData.type === 0">
            {{ $t('reservationDetails.reservationDuration') }}
          </dt>
          <dd v-if="!isBlockedDate && reservationData.type === 0">
            <icon
              name="time"
              primaryColor="color-text-light"
              size="xxs" />
            {{ reservationData.duration }} {{ $t('reservationDetails.minutes') }}
          </dd>
          <dt v-if="(!isBlockedDate || (isBlockedDate && reservationData.name))">
            {{ $t('reservationDetails.reservedBy') }}
          </dt>
          <dd v-if="!isBlockedDate">
            {{ reservationData.user_name }}

            <span
              v-if="reservationData.customer_id && userCanSeeContactData"
              class="client-info"
              @click="$bus.$emit('show-customer-popup', reservationData.customer_id)">
              <icon
                name="info-circle-alt"
                primaryColor="color-text-light"
                strokeColor="color-text-light"
                size="xs" />
              <span class="tooltip">
                {{ $t('common.ui.clientInfo') }}
              </span>
            </span>
          </dd>
          <dd v-if="isBlockedDate && reservationData.name">
            {{ reservationData.name }}
          </dd>
          <dt v-if="!cannotEditDailyReservation && attendancesListEnabled && !isBlockedDate">
            {{ $t('reservationDetails.attendance') }}
          </dt>
          <dd
            v-if="!cannotEditDailyReservation && attendancesListEnabled && !isBlockedDate"
            class="attendance-status">
            <btn
              :type="presentInProgress ? 'loader icon' : 'icon'"
              :title="$t('reservationDetails.clientPresent')"
              @click.native.prevent="setPresentState('present')"
              :disabled="presentInProgress"
              :iconName="!presentInProgress ? 'thumbs-up-circle' : 'loader'"
              :iconColor="reservationData.attendance === 1 ? 'color-success' : 'color-text-lightest'" />
            <btn
              :type="notPresentInProgress ? 'loader icon' : 'icon'"
              :title="$t('reservationDetails.clientNotPresent')"
              @click.native.prevent="setPresentState('not-present')"
              :disabled="notPresentInProgress"
              :iconName="!notPresentInProgress ? 'thumbs-down-circle' : 'loader'"
              :iconColor="reservationData.attendance === 2 ? 'color-danger' : 'color-text-lightest'" />
          </dd>
          <dt v-if="reservationData.phone">
            {{ $t('reservationDetails.customerPhone') }}
          </dt>
          <dd v-if="reservationData.phone">
            <a
              href="#"
              :data-tel="reservationData.phone"
              @click.prevent="$window.location.href='tel:' + reservationData.phone;"
              class="phone">
              <icon
                name="phone"
                primaryColor="color-text-light"
                size="xs" />
              {{ reservationData.phone }}
            </a>
          </dd>
          <dt v-if="reservationData.confirm_email">
            {{ $t('reservationDetails.customerEmail') }}
          </dt>
          <dd v-if="reservationData.confirm_email">
            <a
              href="#"
              :data-email="reservationData.confirm_email"
              :title="reservationData.confirm_email"
              @click.prevent="$window.location.href='mailto:' + reservationData.confirm_email;"
              class="email text-cut">
              <icon
                name="mailbox"
                primaryColor="color-text-light"
                size="xs" />
              {{ reservationData.confirm_email }}
            </a>
          </dd>
          <dt v-if="peopleNumberEnabled && !isBlockedDate">
            {{ $t('common.ui.peopleNumber') }}:
          </dt>
          <dd v-if="peopleNumberEnabled && !isBlockedDate">
            <span class="people-number">
              <icon
                name="profile-big"
                primaryColor="color-text-light"
                size="xs" />
              <span class="people-number-counter">{{ reservationData.people_number }}</span>
            </span>
          </dd>
          <dt v-if="peopleNumberEnabled && !isBlockedDate && reservationData.children_number">
            {{ $t('common.ui.childrenNumber') }}:
          </dt>
          <dd v-if="peopleNumberEnabled && !isBlockedDate && reservationData.children_number">
            <span class="people-number">
              <icon
                name="profile-big"
                primaryColor="color-text-light"
                size="xs" />
              <span class="people-number-counter">{{ reservationData.children_number }}</span>
            </span>
          </dd>
          <dt>{{ $t('reservationDetails.createdAt') }}</dt>
          <dd>{{ reservationData.created_at }}</dd>

          <dt>{{ $t('reservationDetails.createdBy') }}</dt>
          <dd>{{ createdBy }}</dd>
          <template v-if="reservationData.status === 5 && reservationData.cancelled_by_name">
          <dt>{{ $t('reservationDetails.cancelledBy') }}</dt>
          <dd>{{ reservationData.cancelled_by_name }}</dd>
          <dt>{{ $t('reservationDetails.cancelledAt') }}</dt>
          <dd>{{ reservationData.cancellation_date }}</dd>
          </template>

          <dt v-if="reservationData.service">{{ $t('common.ui.service') }}</dt>
          <dd v-if="reservationData.service">
            <span class="service-name-field">
              <span :style="'background: ' + reservationData.service.color" class="service-color"></span>
              {{ reservationData.service.name }}
              <tooltip
                v-if="reservationData.service.category"
                :text="$t('reservationDetails.category') + reservationData.service.category" />
            </span>
          </dd>

          <dt v-if="reservationData.worker">
            {{ $t('common.ui.worker') }}
          </dt>
          <dd v-if="reservationData.worker">
            <avatar
              :worker="reservationData.worker.worker"
              :name="reservationData.worker.name" />
            {{ reservationData.worker.name }}
          </dd>

          <dt
            v-if="reservationData.surveys && reservationData.surveys.length"
            class="is-sublist-label">
            {{ $t('reservationDetails.filledSurveys') }}
          </dt>
          <dd
            v-if="reservationData.surveys && reservationData.surveys.length"
            class="has-sublist">
            <dl>
              <template v-for="(survey, index) of reservationData.surveys">
                <dt :key="'survey-name-' + index">
                  {{ survey.name }}
                </dt>
                <dd :key="'survey-see-results-' + index">
                  <a
                    href="#"
                    @click.prevent="$bus.$emit('show-survey-results-popup', null, survey.questions, survey.scoring)">
                    {{ $t('reservationDetails.seeSurveyResults') }}
                  </a>
                </dd>
              </template>
            </dl>
          </dd>
          <dt v-if="reservationData.lockme_id > 0">{{ $t('reservationDetails.lockmeReservationID') }}</dt>
          <dd v-if="reservationData.lockme_id > 0">{{ reservationData.lockme_id }}</dd>
          <dt v-if="reservationData.from_outlook_calendar || reservationData.from_google_calendar">{{ $t('reservationDetails.importedFrom') }}</dt>
          <dd v-if="reservationData.from_outlook_calendar || reservationData.from_google_calendar">
            <template v-if="reservationData.from_outlook_calendar">{{ $t('reservationDetails.outlookCalendar') }}</template>
            <template v-if="reservationData.from_google_calendar">{{ $t('reservationDetails.googleCalendar') }}</template>
          </dd>

          <dt v-if="reservationData.from_google_calendar && reservationData.google_description && reservationData.google_description.trim() !== ''">
            {{ $t('reservationDetails.noteFromCalendar') }}
          </dt>
          <dd
            v-if="reservationData.from_google_calendar && reservationData.google_description && reservationData.google_description.trim() !== ''"
            key="google-description"
            v-pure-html="googleDescription"></dd>

          <dt v-if="reservationData.from_outlook_calendar && reservationData.outlook_description && reservationData.outlook_description.trim() !== ''">
            {{ $t('reservationDetails.noteFromCalendar') }}
          </dt>
          <dt v-if="reservationData.google_meet_url">
            {{ $t('reservationDetails.googleMeetURL') }}
          </dt>
          <dd
            class="google-meet-link-button"
            v-if="reservationData.google_meet_url">
            <btn
              :type="googleMeetUrlCopiedText ? 'small' : 'small with-icon'"
              :iconName="googleMeetUrlCopiedText ? '' : 'layers-thin'"
              @click.native="copyGoogleMeetURL(reservationData.google_meet_url)">
              <template v-if="googleMeetUrlCopiedText">{{ $t('common.ui.copied') }}</template>
              <template v-if="!googleMeetUrlCopiedText">{{ $t('reservationDetails.copyGoogleMeetLink') }}</template>
            </btn>
          </dd>
          <dd
            v-if="reservationData.from_outlook_calendar && reservationData.outlook_description && reservationData.outlook_description.trim() !== ''"
            key="outlook-description"
            v-pure-html="outlookDescription"></dd>
          <dt
            v-if="reservationData.parameters && reservationData.parameters.length"
            class="is-sublist-label">
            {{ $t('reservationDetails.reservationParams') }}
          </dt>
          <dd
            v-if="reservationData.parameters && reservationData.parameters.length"
            class="has-sublist">
            <dl class="parameters-list">
              <template v-for="(param, index) of reservationData.parameters">
                <dt :key="'param-name-' + index">{{ param.name }}</dt>
                <dd :key="'param-value-' + index">{{ param.value }}</dd>
              </template>
            </dl>
          </dd>

          <dt
            v-if="!isBlockedDate"
            class="is-sublist-label">
            {{ $t('reservationDetails.givenAgreements') }}
          </dt>
          <dd
            v-if="!isBlockedDate"
            class="has-sublist">
            <dl>
              <dt>{{ $t('reservationDetails.tosAndPrivacyPolicy') }}</dt>
              <dd v-if="reservationData.agree_tp" class="is-agree">{{ $t('reservationDetails.yes') }}</dd>
              <dd v-if="!reservationData.agree_tp" class="is-disagree">{{ $t('reservationDetails.no') }}</dd>

              <dt>{{ $t('reservationDetails.emailAgreement') }}</dt>
              <dd v-if="reservationData.agree_newsletter" class="is-agree">{{ $t('reservationDetails.yes') }}</dd>
              <dd v-if="!reservationData.agree_newsletter" class="is-disagree">{{ $t('reservationDetails.no') }}</dd>

              <dt>{{ $t('reservationDetails.phoneAgreement') }}</dt>
              <dd v-if="reservationData.agree_telemarketing" class="is-agree">{{ $t('reservationDetails.yes') }}</dd>
              <dd v-if="!reservationData.agree_telemarketing" class="is-disagree">{{ $t('reservationDetails.no') }}</dd>
            </dl>
          </dd>

          <dt v-if="reservationData.has_periodicity">
            {{ $t('reservationDetails.periodicity') }}
          </dt>
          <dd v-if="reservationData.has_periodicity">
            <periodicity-viewer
              :itemData="reservationData"
              infoBoxPosition="top"
              :parentSelectorWithOverflow="null"
              :rowMode="false"
              :contentMode="'advanced'"
              :isBlockedDate="isBlockedDate"
              :autoRenew="reservationData.periodicity_auto_renew" />
            <template v-if="reservationData.periodicity_auto_renew">
              <br>
              <small>
                <span
                  :title="$t('reservationForm.untilCancelShortNote')"
                  style="cursor: help;">
                  {{ $t('reservationForm.untilCancel') }}
                </span>
                <template v-if="!isBlockedDate">
                    -
                  <a href="#cancel-periodicity" @click.prevent.stop="cancelPeriodicity" class="danger-text">
                    <template v-if="periodicityCancelInProgress">{{ $t('reservationDetails.savingChanges') }}</template>
                    <template v-else>{{ $t('reservationDetails.cancelPeriodicity') }}</template>
                  </a>
                </template>
              </small>
            </template>
          </dd>

          <dt
            v-if="reservationData.payment"
            class="is-sublist-label">
            {{ $t('reservationDetails.payment') }}
          </dt>
          <dd
            v-if="reservationData.payment"
            class="has-sublist">
            <dl>
              <dt v-if="userCanSeeFullPaymentsData || userCanSeeLimitedPaymentsData">
                <template v-if="reservationData.has_periodicity && reservationData.periodicity_payment_type === 0">{{ $t('common.ui.fullCycleCost') }}</template>
                <template v-else>{{ $t('reservationDetails.paymentQuote') }}</template>
              </dt>
              <dd v-if="userCanSeeFullPaymentsData || userCanSeeLimitedPaymentsData">{{ totalPrice }}</dd>

              <dt>{{ $t('common.ui.paymentStatus') }}:</dt>
              <dd>
                <span :class="paymentStatusClass">
                  {{ paymentStatus }}
                </span>

                <a
                  v-if="userCanChangePaymentsStatus && reservationData.status !== 1 && reservationData.status !== 4 && reservationData.status !== 5 && userCanAddReservation"
                  href="#"
                  class="payment-status-edit"
                  @click.prevent="editPaymentStatus">
                  <icon
                    name="edit-circle"
                    class="edit-price"
                    properties="is-clickable"
                    primaryColor="color-notice"
                    size="xxs" />
                  {{ $t('reservationDetails.changePaymentStatus') }}
                </a>
              </dd>

              <dt v-if="reservationData.payment.transaction_id">
                {{ $t('common.ui.transactionID') }}:
              </dt>
              <dd v-if="reservationData.payment.transaction_id">
                {{ reservationData.payment.transaction_id }}
              </dd>

              <dt v-if="reservationData.configuration_payment">
                {{ $t('reservationDetails.paymentMethod') }}
              </dt>
              <dd v-if="reservationData.configuration_payment">
                {{ reservationData.configuration_payment.name }}
              </dd>

              <dt v-if="reservationData.payment.payment_to.date">{{ $t('reservationDetails.paymentDate') }}</dt>
              <dd v-if="reservationData.payment.payment_to.date">{{ reservationData.payment.payment_to.date.replace('.000000', '') }}</dd>

              <dt
                class="payment-link-label"
                v-if="reservationData.payment.status === 'NEW' && reservationData.payment.url">
                {{ $t('reservationDetails.paymentLink') }}
              </dt>
              <dd
                class="payment-link-button"
                v-if="reservationData.payment.status === 'NEW' && reservationData.payment.url">
                <btn
                  :type="paymentUrlCopiedText ? 'small' : 'small with-icon'"
                  :iconName="paymentUrlCopiedText ? '' : 'layers-thin'"
                  @click.native="copyPaymentURL(reservationData.payment.url)">
                  <template v-if="paymentUrlCopiedText">{{ $t('common.ui.copied') }}</template>
                  <template v-if="!paymentUrlCopiedText">{{ $t('reservationDetails.copyPaymentLink') }}</template>
                </btn>
              </dd>

              <template v-if="reservationData.payment.discount_code">
                <dt>{{ $t('reservationDetails.usedDiscountCode') }}</dt>
                <dd>{{ reservationData.payment.discount_code }}</dd>
                <dt>{{ $t('reservationDetails.appliedDiscountQuote') }}</dt>
                <dd>{{ discountQuote }}</dd>
              </template>

              <dt
                v-if="showInvoiceInfo">
                {{ $t('reservationDetails.invoiceOrReceipt') }}
              </dt>

              <dd v-if="showInvoiceInfo">
                <invoice-downloader
                  :inquiryID="reservationData.id"
                  :documentType="reservationData.invoice.type"
                  :invoiceID="reservationData.invoice.id"
                  :invoiceExtID="reservationData.invoice.ext_id"
                  :invoiceNumber="reservationData.invoice.number"
                  :isOldInvoice="reservationData.invoice.old_invoice" />
              </dd>
            </dl>
          </dd>

          <dt
            v-if="reservationData.type === 0 && reservationData.service && reservationData.service.tickets"
            class="is-sublist-label">
            {{ $t('reservationDetails.qrCodeTickets') }}
          </dt>
          <dd
            v-if="reservationData.type === 0 && reservationData.service && reservationData.service.tickets"
            class="has-sublist">
            <dl>
              <dt>{{ $t('reservationDetails.usedQrCodeTickets') }}</dt>
              <dd>
                <template v-if="reservationData.service.ticket_used > 0">{{ reservationData.service.ticket_used }} / {{ reservationData.people_number }}</template>
                <template v-else>{{ $t('reservationDetails.no') }}</template>
              </dd>

              <dt>{{ $t('reservationDetails.qrCodeTicketsDateOfUse') }}</dt>
              <dd
                v-for="(date, index) of reservationData.service.ticket_used_date"
                :key="'ticket-used-date-' + index">
                {{ date }}
              </dd>
              <dd v-if="reservationData.service.ticket_used_date.length === 0">
                {{ $t('common.ui.none') }}
              </dd>
            </dl>
          </dd>

          <dt v-if="isCompanyBlockedDate && reservationData.description">
            {{ $t('reservationDetails.blockadeDescription') }}:
          </dt>
          <dd v-if="isCompanyBlockedDate && reservationData.description">
            {{ reservationData.description }}
          </dd>
        </dl>
      </div>

      <btn
        v-if="!isLoading && !cannotEditDailyReservation && userCanAddReservation && !isBlockedDate"
        @click.native="editReservation(reservationID)"
        type="popup-left primary">
        {{ $t('common.ui.edit') }}
      </btn>

      <btn
        v-if="!isLoading && !cannotEditDailyReservation && userCanAddReservation && isBlockedDate && !isCompanyBlockedDate"
        @click.native="convertIntoReservation(reservationID)"
        :disabled="convertInProgress"
        type="popup-left primary">
        {{ $t('reservationDetails.changeIntoReservation') }}
      </btn>

      <btn
        v-if="!isLoading"
        :type="cannotEditDailyReservation || !userCanAddReservation || isCompanyBlockedDate ? 'popup-full' : 'popup-right'"
        @click.native="close">
        {{ $t('common.ui.close') }}
      </btn>
    </div>
  </div>
</template>

<script>
import Avatar from './../common/Avatar';
import CalendarUtils from './../helpers/CalendarUtils';
import Configuration from './../mixins/Configuration';
import Copy from 'copy-to-clipboard';
import CurrencyUtils from './../helpers/CurrencyUtils';
import ErrorUtils from './../helpers/ErrorUtils.js';
import InvoiceDownloader from './../ui/InvoiceDownloader';
import PeriodicityViewer from '../common/PeriodicityViewer.vue';
import PopupZindex from './../mixins/PopupZindex';
import ReservationFormUtils from './../helpers/ReservationFormUtils';
import ReservationStatusChange from './../mixins/ReservationStatusChange';
import Tooltip from './../common/Tooltip';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';
import StripTags from 'locutus/php/strings/strip_tags';

export default {
  name: 'reservation-details-popup',
  mixins: [
    Configuration,
    PopupZindex,
    ReservationStatusChange,
    UserTypes,
    WordVariations
  ],
  components: {
    'avatar': Avatar,
    'invoice-downloader': InvoiceDownloader,
    'periodicity-viewer': PeriodicityViewer,
    'tooltip': Tooltip
  },
  data () {
    return {
      type: '',
      isBlockedDate: false,
      isCompanyBlockedDate: false,
      reservationID: 0,
      isReservation: false,
      isLoading: true,
      isVisible: false,
      reservationData: false,
      paymentUrlCopiedText: false,
      googleMeetUrlCopiedText: false,
      acceptInProgress: false,
      rejectInProgress: false,
      presentInProgress: false,
      notPresentInProgress: false,
      convertInProgress: false,
      unblockInProgress: false,
      periodicityCancelInProgress: false
    };
  },
  computed: {
    createdBy () {
      if (this.reservationData.created_by_name) {
        return this.reservationData.created_by_name;
      }

      if (this.reservationData.created_by_admin) {
        return this.$t('reservationDetails.byAdministrator');
      }

      return this.$t('reservationDetails.byClient');
    },
    reservationDate () {
      if (!this.isBlockedDate && this.reservationData.date) {
        return CalendarUtils.getDayNameFromDate(this.reservationData.date) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.date, false);
      } else if (this.isBlockedDate && this.reservationData.start_at) {
        let showHour = this.isCompanyBlockedDate && !this.reservationData.is_daily;
        return CalendarUtils.getDayNameFromDate(this.reservationData.start_at) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.start_at, showHour);
      }

      return '-';
    },
    reservationDateStart () {
      if (this.reservationData.start_at) {
        let showHour = this.isCompanyBlockedDate && !this.reservationData.is_daily;
        return CalendarUtils.getDayNameFromDate(this.reservationData.start_at) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.start_at, showHour);
      }

      return '-';
    },
    reservationDateEnd () {
      if (this.reservationData.end_at) {
        return CalendarUtils.getDayNameFromDate(this.reservationData.end_at) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.end_at, false);
      }

      return '-';
    },
    blockedToDate () {
      if (this.isBlockedDate && this.reservationData.blocked_to) {
        return CalendarUtils.getDayNameFromDate(this.reservationData.blocked_to) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.blocked_to, true);
      }

      return '-';
    },
    reservationWaitingListNotificationDate () {
      if (this.reservationData.waiting_list_notified) {
        return CalendarUtils.getDayNameFromDate(this.reservationData.waiting_list_notified) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.waiting_list_notified, false);
      } else {
        return '-';
      }
    },
    reservationStatus () {
      if (this.reservationData.status === 1) {
        return this.$t('reservationDetails.waitsForAccept');
      }

      if (this.reservationData.status === 3) {
        if (
          this.reservationData.payment &&
          this.reservationData.payment.status === 'CARD_REGISTERED'
        ) {
          return this.$t('reservationDetails.cardRegistered');
        }

        if (
          this.reservationData.payment &&
          !(this.reservationData.payment.status === 'COMPLETED' || this.reservationData.payment.status === 'CASH')
        ) {
          return this.$t('reservationDetails.waitsForPayment');
        }

        return this.$t('reservationDetails.accepted');
      }

      if (this.reservationData.status === 4) {
        return this.$t('reservationDetails.rejected');
      }

      if (this.reservationData.status === 5) {
        return this.$t('reservationDetails.cancelled');
      }

      if (this.reservationData.status === 6) {
        return this.$t('reservationDetails.waitingList');
      }

      return '-';
    },
    reservationStatusClass () {
      if (this.reservationData.status === 1) {
        return 'status status-is-waiting-for-approve';
      }

      if (this.reservationData.status === 3) {
        if (
          this.reservationData.payment &&
          !(this.reservationData.payment.status === 'COMPLETED' || this.reservationData.payment.status === 'CASH')
        ) {
          return 'status status-is-waiting-for-payment';
        }

        return 'status status-is-accepted';
      }

      if (this.reservationData.status === 4) {
        return 'status status-is-rejected';
      }

      if (this.reservationData.status === 5) {
        return 'status status-is-cancelled';
      }

      if (this.reservationData.status === 6) {
        return 'status status-is-waiting-list';
      }

      return 'status status-is-unknown';
    },
    startTime () {
      if (this.reservationData.start_at) {
        if (this.reservationData.start_at.split(' ')[1]) {
          let time = this.reservationData.start_at.split(' ')[1].split(':');
          return [time[0], time[1]].join(':');
        } else {
          return this.reservationData.start_at;
        }
      }

      return '-';
    },
    endTime () {
      if (this.reservationData.end_at) {
        if (this.reservationData.end_at.split(' ')[1]) {
          let time = this.reservationData.end_at.split(' ')[1].split(':');
          return [time[0], time[1]].join(':');
        } else {
          return this.reservationData.end_at;
        }
      }

      return '-';
    },
    paymentStatus () {
      switch (this.reservationData.payment.status) {
        case 'NEW': return this.$t('reservationDetails.paymentStatusNew');
        case 'PENDING': return this.$t('reservationDetails.paymentStatusPending');
        case 'COMPLETED': return this.$t('reservationDetails.paymentStatusCompleted');
        case 'CANCELED': return this.$t('reservationDetails.paymentStatusCancelled');
        case 'CASH': return this.$t('common.ui.paidWithCash');
        case 'CARD_REGISTERED': return this.$t('reservationDetails.paymentStatusCardRegistered');
        case 'PAYMENT_REGISTRATION': return this.$t('reservationDetails.paymentStatusPaymentRegistration');
        case 'PAYMENT_COLLECTED': return this.$t('reservationDetails.paymentStatusPaymentCollected');
        default: return '-';
      }
    },
    paymentStatusClass () {
      switch (this.reservationData.payment.status) {
        case 'NEW': return 'is-waiting';
        case 'PENDING': return 'is-waiting';
        case 'COMPLETED': return 'is-confirmed';
        case 'CANCELED': return 'is-rejected';
        case 'CASH': return 'is-confirmed';
        case 'CARD_REGISTERED': return 'is-waiting';
        case 'PAYMENT_REGISTRATION': return 'is-waiting';
        case 'PAYMENT_COLLECTED': return 'is-confirmed';
        default: return '';
      }
    },
    priceCurrency () {
      if (this.reservationData.payment && this.reservationData.payment.currency) {
        return this.reservationData.payment.currency;
      } else if (this.reservationData.configuration_payment) {
        return this.reservationData.configuration_payment.currency;
      }

      return this.$root.defaultCurrency;
    },
    totalPrice () {
      return CurrencyUtils.getQuote(this.reservationData.payment.price, this.priceCurrency);
    },
    discountQuote () {
      if (this.reservationData.payment.discount_code) {
        return CurrencyUtils.getQuote(this.reservationData.payment.discount_value, this.reservationData.payment.currency);
      }

      return '';
    },
    outlookDescription () {
      return StripTags(this.reservationData.outlook_description, '<br>').replace(/(?:\r\n|\r|\n)/gmi, '<br>').replace(/^(<br>){1,}|(<br>){1,}$/gmi, '').replace(/(<br>){1,}/gmi, '<br>');
    },
    googleDescription () {
      return StripTags(this.reservationData.google_description, '<br>').replace(/(?:\r\n|\r|\n)/gmi, '<br>').replace(/^(<br>){1,}|(<br>){1,}$/gmi, '').replace(/(<br>){1,}/gmi, '<br>');
    },
    cannotEditDailyReservation () {
      return (this.reservationData.is_daily || this.reservationData.type === 1) && !this.dailyReservationsEnabled;
    },
    isCyclic () {
      return this.reservationData.has_periodicity;
    },
    showInvoiceInfo () {
      return this.userCanSeeInvoices && this.userCanManageOwn && this.reservationData.invoice;
    }
  },
  mounted () {
    this.$bus.$on('show-reservation-details-popup', this.show);
    this.$bus.$on('hide-reservation-details-popup', this.close);
    this.$bus.$on('invoice-downloader-update-data', this.updateInvoiceData);
    this.$bus.$on('reservation-details-popup-reload-needed', this.reload);
  },
  methods: {
    show (id, type) {
      this.reservationID = id;
      this.type = type;
      this.convertInProgress = false;
      this.isLoading = true;
      this.isVisible = true;
      this.loadData();
      this.increasePopupZindex();
    },
    loadData () {
      if (['blocking', 'cart', 'company'].indexOf(this.type) > -1) {
        this.loadBlockedDateData();
      } else {
        this.loadReservationData();
      }
    },
    loadReservationData () {
      this.$http.post('/panel/general/get-reservation', {
        id: this.reservationID
      }).then(response => {
        this.reservationData = response.data;

        if (this.reservationData.status !== 1) {
          this.isReservation = true;
        }

        this.isLoading = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/general/get-reservation', this.$router, this.$bus, this.loadData.bind(this));
        this.isLoading = false;
      });
    },
    loadBlockedDateData () {
      this.$http.get('/panel/blocked-termins/item/' + parseInt(this.reservationID, 10)).then(response => {
        if (response.data.success === 0) {
          this.close();
          return;
        }

        this.reservationData = response.data.data;
        this.isBlockedDate = true;

        if (this.reservationData.type === 'company') {
          this.isCompanyBlockedDate = true;
        }

        this.isLoading = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/blocked-termins/item/' + parseInt(this.reservationID, 10), this.$router, this.$bus, this.loadBlockedDateData.bind(this));
        this.isLoading = false;
      });
    },
    close () {
      this.reservationID = 0;
      this.type = '';
      this.isBlockedDate = false;
      this.isCompanyBlockedDate = false;
      this.isReservation = false;
      this.isVisible = false;
      this.isLoading = true;
      this.reservationData = false;
      this.paymentUrlCopiedText = false;
      this.googleMeetUrlCopiedText = false;
      this.acceptInProgress = false;
      this.rejectInProgress = false;
      this.presentInProgress = false;
      this.notPresentInProgress = false;
      this.convertInProgress = false;
      this.unblockInProgress = false;
      this.periodicityCancelInProgress = false;
      this.removePopupZindex();
    },
    reload () {
      if (this.reservationID !== 0) {
        this.isLoading = true;
        this.loadData();
      }
    },
    editReservation (reservationID) {
      this.close();
      this.$bus.$emit('search-popup-hide');

      if (this.$route.path.indexOf('/dashboard') > -1) {
        this.$root.formReferrer = 'dashboard';
      }

      this.$router.push('/reservation/edit/' + reservationID);
    },
    copyPaymentURL (text) {
      Copy(text);
      this.paymentUrlCopiedText = true;

      setTimeout(() => {
        this.paymentUrlCopiedText = false;
      }, 2000);
    },
    copyGoogleMeetURL (text) {
      Copy(text);
      this.googleMeetUrlCopiedText = true;

      setTimeout(() => {
        this.googleMeetUrlCopiedText = false;
      }, 2000);
    },
    changeReservationDate () {
      let dailyEndAvailable = false;

      if (this.reservationData.service) {
        dailyEndAvailable = this.reservationData.service.daily_end_available;
      }

      this.$bus.$emit('show-change-date-popup', 'edit-date', this.reservationData, dailyEndAvailable);
    },
    copyReservation () {
      let dailyEndAvailable = false;

      if (this.reservationData.service) {
        dailyEndAvailable = this.reservationData.service.daily_end_available;
      }

      this.$bus.$emit('show-change-date-popup', 'copy-reservation', this.reservationData, dailyEndAvailable);
    },
    editPaymentStatus () {
      this.$bus.$emit('show-payment-status-form', {
        id: this.reservationData.id,
        status: this.reservationData.payment.status,
        isCash: this.reservationData.payment.system === 'cash',
        isCyclic: this.reservationData.has_periodicity && this.reservationData.periodicity_payment_type === 0
      });
    },
    setPresentState (newState) {
      let status = 0;

      if (newState === 'present') {
        this.presentInProgress = true;

        if (this.reservationData.attendance !== 1) {
          status = 1;
        }
      } else if (newState === 'not-present') {
        this.notPresentInProgress = true;

        if (this.reservationData.attendance !== 2) {
          status = 2;
        }
      }

      this.$http.post('panel/attendances/status/' + this.reservationData.id + '/' + status).then(response => {
        let messageText = this.$t('reservationDetails.clientNotPresentSaved');

        if (newState === 'present') {
          messageText = this.$t('reservationDetails.clientPresentSaved');
        }

        if (status === 0) {
          messageText = this.$t('reservationDetails.clientPresentReset');
        }

        if (newState === 'present') {
          this.presentInProgress = false;
        } else if (newState === 'not-present') {
          this.notPresentInProgress = false;
        }

        this.reservationData.attendance = status;
        this.$bus.$emit('notification-display', {
          type: 'success',
          message: messageText
        });
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, 'panel/attendances/status/' + this.reservationData.id + '/' + status, this.$router, this.$bus, this.setPresentState.bind(this));

        if (newState === 'present') {
          this.presentInProgress = false;
        } else if (newState === 'not-present') {
          this.notPresentInProgress = false;
        }
      });
    },
    convertIntoReservation () {
      this.convertInProgress = true;

      if (this.reservationData.parent_id) {
        this.$http.get('/panel/blocked-termins/item/' + parseInt(this.reservationData.parent_id, 10)).then(response => {
          this.reservationData = response.data.data;
          this.goToReservationForm();
        }).catch(error => {
          ErrorUtils.handleSubmitError(error, '/panel/blocked-termins/item/' + parseInt(this.reservationData.parent_id, 10), this.$router, this.$bus, this.convertIntoReservation.bind(this));
        });
      } else {
        this.goToReservationForm();
      }
    },
    goToReservationForm () {
      let reservationConfig = {
        selectedDateType: 'date',
        plugin_inquiry_id: this.reservationData.plugin_inquiry_id
      };

      if (this.workersEnabled && this.reservationData.worker_id) {
        reservationConfig.worker = this.reservationData.worker_id;
      }

      if (this.servicesEnabled && this.reservationData.service_id) {
        reservationConfig.service = this.reservationData.service_id;
      }

      if (this.reservationData.start_at) {
        reservationConfig.date = ReservationFormUtils.fixDateFormat(this.reservationData.start_at.split(' ')[0]);
        reservationConfig.hour = this.reservationData.start_at.split(' ')[1].split(':').slice(0, 2).join(':');
      }

      if (this.reservationData.is_daily || this.reservationData.type === 1) {
        reservationConfig.days = this.reservationData.days;

        if (this.reservationData.children_number) {
          reservationConfig.children = this.reservationData.children_number;
        }
      }

      if (this.peopleNumberEnabled && this.reservationData.people_number) {
        reservationConfig.people = this.reservationData.people_number;
      }

      if (this.reservationData.periodicity && this.reservationData.periodicity.length) {
        reservationConfig.periodicity = 1;
      }

      if (this.reservationData.periodicity_id) {
        reservationConfig.periodicity_id = this.reservationData.periodicity_id;
      }

      if (typeof this.reservationData.periodicity_number !== 'undefined') {
        reservationConfig.periodicity_number = this.reservationData.periodicity_number;
      }

      if (typeof this.reservationData.periodicity_type !== 'undefined') {
        reservationConfig.periodicity_type = this.reservationData.periodicity_type;
      }

      if (this.reservationData.name) {
        reservationConfig.name = this.reservationData.name;
      }

      if (this.reservationData.confirm_email) {
        reservationConfig.email = this.reservationData.confirm_email;
      }

      if (this.reservationData.phone) {
        reservationConfig.phone = this.reservationData.phone;
      }

      if (this.reservationData.duration) {
        reservationConfig.duration = this.reservationData.duration;
      }

      if (this.reservationData.custom_duration_id) {
        reservationConfig.custom_duration_id = this.reservationData.custom_duration_id;
      }

      if (this.reservationData.periodicity_payment_type) {
        reservationConfig.periodicity_payment_type = this.reservationData.periodicity_payment_type;
      }

      if (this.reservationData.periodicity_auto_renew) {
        reservationConfig.periodicity_auto_renew = this.reservationData.periodicity_auto_renew;
      }

      this.$router.push({
        name: 'ReservationFormAddFromBlockedDate',
        query: reservationConfig
      });

      this.close();
    },
    cancelPeriodicity () {
      if (this.periodicityCancelInProgress) {
        return;
      }

      let futureInquiriesCount = 0;
      let futureInquiriesDate = '';
      this.periodicityCancelInProgress = true;

      if (this.reservationData.has_periodicity) {
        futureInquiriesCount = this.reservationData.total_future_inquiries.total;
        futureInquiriesDate = this.reservationData.total_future_inquiries.get_by_date;
      }

      this.$bus.$emit('confirm-display', {
        title: this.$t('reservationDetails.cancelPeriodicityConfirmTitle'),
        desc: this.$t('reservationDetails.cancelPeriodicityConfirmDesc'),
        cancelLabel: this.$t('common.ui.cancel'),
        cancelClick: this.cancelPeriodicityReject.bind(this),
        checkbox: true,
        checkboxLabel: this.reservationData.payment ? this.$tc('reservationDetails.cancelFutureWithPayments', futureInquiriesCount, { count: futureInquiriesCount, date: futureInquiriesDate }) : this.$tc('reservationDetails.cancelFutureWithoutPayments', futureInquiriesCount, { count: futureInquiriesCount, date: futureInquiriesDate }),
        checkboxDefaultValue: false,
        okLabel: this.$t('reservationDetails.okCancelPeriodicity'),
        okClick: this.cancelPeriodicityConfirmed.bind(this)
      });
    },
    cancelPeriodicityReject () {
      this.periodicityCancelInProgress = false;
    },
    cancelPeriodicityConfirmed (removeFuture = false) {
      let idToCancel = this.reservationData.id;

      if (this.reservationData.parent_id) {
        idToCancel = this.reservationData.parent_id;
      }

      this.$http.post('/panel/reservations/cancel-auto-renew', {
        id: parseInt(idToCancel, 10),
        remove_future: +removeFuture
      }).then(response => {
        this.$bus.$emit('reservation-details-popup-reload-needed');

        if (!response || !response.data || !response.data.success) {
          this.$bus.$emit('alert-display', {
            title: '',
            desc: this.$t('reservationDetails.periodicityCancelFailed'),
            okLabel: this.$t('common.ui.ok'),
            okClick: () => true,
            isDanger: true
          });
        }

        this.periodicityCancelInProgress = false;

        if (removeFuture) {
          this.close();
          this.$bus.$emit('calendar-layout-reload-needed');
          this.$bus.$emit('updated-reservation-status');
          this.$bus.$emit('calendar-day-popup-reload-reservations');
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/reservations/cancel-auto-renew' + parseInt(this.reservationData.parent_id, 10), this.$router, this.$bus, this.cancelPeriodicity.bind(this));
        this.periodicityCancelInProgress = false;
      });
    },
    updateInvoiceData (invoiceData) {
      if (this.reservationData.id !== invoiceData.inquiry_id) {
        return;
      }

      this.reservationData.invoice.type = invoiceData.document_type;
      this.reservationData.invoice.id = invoiceData.invoice_id;
      this.reservationData.invoice.ext_id = invoiceData.invoice_external_id;
      this.reservationData.invoice.number = invoiceData.invoice_number;
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-reservation-details-popup', this.show);
    this.$bus.$off('hide-reservation-details-popup', this.close);
    this.$bus.$off('invoice-downloader-update-data', this.updateInvoiceData);
    this.$bus.$off('reservation-details-popup-reload-needed', this.reload);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.reservation-details-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  max-height: 90vh;
  padding: 3rem 4rem 8rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 720px;
  z-index: 10000;

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;
    z-index: 10000;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;

      .confirm {
        transform: translateX(-50%) translateY(-50%);
      }
    }
  }

  &.is-visible {
    display: block;
  }

  &.is-loading {
    h2,
    p,
    dl {
      opacity: 0;
    }
  }

  .danger-text {
    color: $color-danger;
  }

  &-status,
  &-status-label {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;

    .button {
      margin: 0 0 0 1rem;
    }
  }

  .reservation-details-popup-empty-state {
    align-items: center;
    color: $color-text-light;
    display: flex;
    font-size: 2.4rem;
    height: 100%;
    justify-content: center;
    left: 0;
    padding: 3.2rem 0;
    position: absolute;
    text-align: center;
    top: 0;
    width: 100%;
    z-index: 10;
  }

  & > h2 {
    align-items: flex-end;
    display: flex;
    margin: 0;

    & > small {
      color: $color-text-light;
      margin-left: 1rem;
    }

    .button {
      position: relative;
      top: 2px;

      &:first-of-type {
        margin-left: auto;
      }

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .edit-link {
    font-size: 1.4rem;
    margin-left: auto;

    .icon {
      position: relative;
      top: 1px;
    }

    &:active,
    &:focus,
    &:hover {
      opacity: .75;
    }
  }

  &-content {
    max-height: 77vh;
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    & > dl {
      display: flex;
      flex-wrap: wrap;

      & > dt {
        border-top: 1px solid $color-border;
        font-weight: bold;
        margin: 0;
        padding: 1rem 0;
        width: 200px;
      }

      & > dd {
        border-top: 1px solid $color-border;
        margin: 0;
        padding: 1rem 0;
        width: calc(100% - 200px);

        .invoice-downloader {
          margin-top: 0;
        }
      }

      .avatar {
        display: inline-block;
        margin: -.5rem .5rem -.5rem -.5rem;
        transform: scale(.65);
        vertical-align: middle;
      }

      dl {
        display: flex;
        flex-wrap: wrap;
        font-size: 1.3rem;
        margin: -.16666666rem 0 0 0;

        dt {
          border-top: 1px solid $color-border;
          font-weight: 600;
          padding: .5rem 0;
          width: 40%;

          &:first-of-type {
            border-top: none;
          }
        }

        dd {
          border-top: 1px solid $color-border;
          margin: 0;
          padding: .5rem 0 .5rem 1rem;
          width: 60%;

          &:first-of-type {
            border-top: none;
          }
        }
      }
    }
  }

  .button-popup-left {
    border-radius: 0 0 0 $border-radius-small;
  }

  .button-popup-right {
    border-radius: 0 0 $border-radius-small 0;
  }

  .button-popup-full {
    border-radius: 0 0 $border-radius-small $border-radius-small;
    color: $color-text;
  }

  &-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
  }

  .people-number {
    &-counter {
      background: $color-text-light;
      border: 2px solid $color-light;
      border-radius: 1rem;
      color: $color-light;
      font-size: 1.2rem;
      font-weight: bold;
      margin-left: -.7rem;
      padding: .125rem .5rem;
      position: relative;
      top: -.4rem;
    }
  }

  .email,
  .phone,
  .client-info {
    padding: 0 .25rem;
    position: relative;

    .icon {
      margin-right: .5rem;
      position: relative;
      top: 2px;
    }
  }

  .client-info {
    cursor: pointer;
    will-change: contents;

    &:hover {
      .tooltip {
        opacity: 1;
        top: -3rem;
      }
    }
  }

  .tooltip {
    background: $color-dark;
    border-radius: $border-radius-small;
    color: $color-light;
    display: block;
    font-size: 1.4rem;
    font-weight: 400;
    left: 50%;
    opacity: 0;
    padding: .5rem 1rem;
    pointer-events: none;
    position: absolute;
    top: -2.5rem;
    transform: translateX(-50%);
    transition: all .25s ease-out;
    white-space: nowrap;

    &:after {
      border: .5rem solid $color-dark;
      border-left-color: transparent;
      border-right-color: transparent;
      border-bottom-color: transparent;
      bottom: -1rem;
      content: "";
      height: 1rem;
      left: 50%;
      position: absolute;
      transform: translateX(-50%);
      width: 1rem;
    }
  }

  .service-color {
    border-radius: 50%;
    display: inline-block;
    height: 2rem;
    left: 1px;
    margin-right: 1rem;
    margin-top: -.5rem;
    position: relative;
    top: 4px;
    width: 2rem;
  }

  .price {
    .payment-status {
      .is-paid {
        color: $color-success;
      }

      .is-not-paid {
        color: $color-danger;
      }
    }
  }

  .is-agree,
  .is-confirmed {
    color: $color-success;
  }

  .is-disagree,
  .is-rejected {
    color: $color-danger;
  }

  .is-waiting {
    color: $color-text-light;
  }

  .is-agree,
  .is-disagree,
  .is-confirmed,
  .is-rejected,
  .is-waiting {
    padding-right: 1rem;
  }

  .payment-status-edit {
    .icon {
      position: relative;
      top: 1px;
    }
  }

  .payment-link-label {
    padding-top: 1.2rem;
  }

  .google-meet-link-button .button,
  .payment-link-button .button {
    margin-left: 0;
  }

  .service-name-field {
    cursor: pointer;
    position: relative;

    &:hover {
      .tooltip {
        opacity: 1;
        top: 50%;

        &.tooltip-top {
          top: -30px;
        }
      }
    }
  }

  .status {
    font-weight: bold;

    &.status-is-waiting-for-approve,
    &.status-is-waiting-for-payment {
      color: $color-notice;
    }
    &.status-is-accepted {
      color: $color-success;
    }
    &.status-is-rejected,
    &.status-is-cancelled {
      color: $color-danger;
    }
    &.status-is-waiting-list {
      color: $color-warning;
    }
    &.status-is-unknown {
      color: $color-dark;
    }
  }

  .attendance-status {
    display: flex;
    justify-content: flex-start;

    .button {
      margin: 0;
    }

    .button + .button {
      margin-left: 1rem!important;
    }
  }
}

@media (max-height: 760px) and (min-width: 800px) {
  .reservation-details-popup-content {
    max-height: 60vh;
    margin-top: 3rem;
  }
}

@media (max-width: 800px) {
  .reservation-details-popup {
    border-radius: 0;
    height: 100%;
    left: 0;
    max-height: 100%;
    padding: 2rem 2rem 8rem 2rem;
    top: 0;
    transform: none;
    width: 100vw;

    .reservation-details-popup-close {
      display: none;
    }

    .button-popup-left,
    .button-popup-right {
      border-radius: 0;
    }

    .reservation-details-popup-content {
      height: 100%;
      margin-top: 2rem;
      max-height: 97.5%;
    }
  }
}

@media (max-width: 640px) {
  .reservation-details-popup {
    & > h2 {
      font-size: 20px;
      position: relative;
      top: -10px;

      & > small {
        position: relative;
        top: -2px;
      }

      & > .button {
        left: 0;
        position: absolute;
        top: 30px;
        width: calc(50% - .5rem);

        & + .button {
          left: auto;
          right: 0;
        }
      }
    }

    .reservation-details-popup-content > dl > dt.is-sublist-label,
    .reservation-details-popup-content > dl > dd.has-sublist {
      width: 100%;

      dl > dt {
        width: calc(200px - 1rem);
      }

      dl > dd {
        width: calc(100% - 200px + 1rem);
      }
    }

    .reservation-details-popup-content {
      height: 100%;
      margin-top: 0rem;
    }

    &.has-header-buttons {
      .reservation-details-popup-content {
        height: calc(100% - 30px);
        margin-top: 3.5rem;
      }
    }
  }
}

@media (max-width: 480px) {
  .reservation-details-popup {
    &-content {
      & > dl > dt,
      & > dl > dd {
        width: 100%;
      }

      .has-sublist {
        .parameters-list {
          & > dt,
          & > dd {
            padding-left: 0;
            width: 100%!important;
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/ReservationDetails.vue